import type { ReactNode } from 'react';
import classnames from 'classnames';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback, InputContainer } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';
import { RadioInput } from './Radio';

interface IRadioGroupInputProps<T extends string> {
    automation?: string;
    children?: ReactNode;
    customOption?: JSX.Element;
    disabled?: boolean;
    error?: string;
    labels?: string[] | JSX.Element[];
    name: string;
    onChange: (value: T) => void;
    onDoValidate?: () => void;
    options: T[];
    radioButtonsStacked?: boolean;
    value: T | null;
}

/**
 * @deprecated Use RadioGroupFormGroupField
 */
export function RadioGroupInput<T extends string>(props: FormContextProps<IRadioGroupInputProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                return (
                    <div
                        className={classnames(
                            'radiobutton-group',
                            properties.automation ? 'automation-' + properties.automation : ''
                        )}
                        data-automation-test={properties.automation}
                    >
                        {properties.options.map((option, i) => (
                            <RadioInput
                                disabled={properties.disabled}
                                key={properties.name + option}
                                label={properties.labels && properties.labels[i] ? properties.labels[i] : option}
                                name={properties.name}
                                onChange={async (value) => {
                                    await properties.onChange(value as T);
                                    if (properties.onDoValidate) {
                                        await properties.onDoValidate();
                                    }
                                }}
                                onValue={option}
                                radioButtonsStacked={properties.radioButtonsStacked}
                                value={properties.value}
                            >
                                <RenderChildren<T>
                                    childrenToRender={props.children}
                                    onValue={option}
                                    value={properties.value}
                                />
                            </RadioInput>
                        ))}
                        {props.customOption && <>{props.customOption}</>}
                        <InputContainer>
                            <Feedback error={properties.error} />
                        </InputContainer>
                    </div>
                );
            }}
        </FormContextConsumer>
    );
}

function RenderChildren<T extends string>(props: { childrenToRender?: ReactNode; onValue: T; value: T | null }) {
    if (props.value === props.onValue && props.childrenToRender) {
        return <>{props.childrenToRender}</>;
    }

    return <></>;
}

/**
 * @deprecated Use RadioGroup instead
 * `import { RadioGroup } from '@ksyos/design-system';`
 */
export function RadioGroupField<T extends string>(props: FormContextProps<IRadioGroupInputProps<T> & IFieldProps>) {
    return (
        <Field {...props}>
            <RadioGroupInput<T> {...props} />
        </Field>
    );
}
